import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";

/**
 * CalendarView — Google Calendar-style Day/Week/Month view of Work Orders.
 * v1.9.11 · rediseño estilo Google Calendar (números grandes de día,
 * badge circular azul para "hoy", bloques de evento con borde izquierdo
 * color-coded, vista mes con "+ N más" cuando hay más de 3 OTs).
 *
 * Props:
 *   - orders: array
 *   - onOpenOrder: (id) => void
 */
const STATUS_COLOR = {
  pendiente_agendamiento: { border: "#94A3B8", bg: "#F1F5F9", text: "#334155" },
  asignada:              { border: "#2563EB", bg: "#DBEAFE", text: "#1E3A8A" },
  en_ruta:               { border: "#0891B2", bg: "#CFFAFE", text: "#164E63" },
  en_ejecucion:          { border: "#0EA5E9", bg: "#E0F2FE", text: "#075985" },
  pendiente_fiscalizacion:{ border: "#D97706", bg: "#FEF3C7", text: "#78350F" },
  validada:              { border: "#16A34A", bg: "#DCFCE7", text: "#14532D" },
  con_observaciones:     { border: "#DC2626", bg: "#FEE2E2", text: "#7F1D1D" },
  rechazada:             { border: "#B91C1C", bg: "#FEE2E2", text: "#7F1D1D" },
  prefacturada:          { border: "#059669", bg: "#D1FAE5", text: "#065F46" },
  cerrada:               { border: "#0A3747", bg: "#E2E8F0", text: "#0A3747" },
  cancelada:             { border: "#6B7280", bg: "#F3F4F6", text: "#374151" },
};

const MODES = [
  { key: "day",   label: "Día" },
  { key: "week",  label: "Semana" },
  { key: "month", label: "Mes" },
];

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00 → 19:00
const DAYS_ES_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// v2.1.4 · SLA por prioridad (horas de duración estimada de la OT)
const SLA_HOURS_BY_PRIORITY = { P1: 1, P2: 1.5, P3: 2 };
const getSlaHours = (order) => SLA_HOURS_BY_PRIORITY[order?.priority] || 1.5;

// Devuelve HH:MM
const fmtHHMM = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const addDays = (d, days) => { const n = new Date(d); n.setDate(n.getDate() + days); return n; };
const startOfWeek = (d) => { const n = new Date(d); n.setDate(n.getDate() - n.getDay()); n.setHours(0,0,0,0); return n; };
const startOfMonthGrid = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const start = new Date(first);
  start.setDate(start.getDate() - first.getDay());
  return start;
};

export default function CalendarView({ orders, onOpenOrder }) {
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const todayISO = toISODate(new Date());

  const shiftBy = (dir) => {
    if (mode === "day") setAnchor(addDays(anchor, dir));
    else if (mode === "week") setAnchor(addDays(anchor, dir * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };

  const headerLabel = useMemo(() => {
    if (mode === "day") {
      return anchor.toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", weekday: "long", day: "numeric", month: "long", year: "numeric" });
    }
    if (mode === "week") {
      const s = startOfWeek(anchor); const e = addDays(s, 6);
      return `${s.getDate()} – ${e.getDate()} ${s.toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", month: "long", year: "numeric" })}`;
    }
    return anchor.toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", month: "long", year: "numeric" });
  }, [mode, anchor]);

  const ordersByISO = useMemo(() => {
    const m = {};
    for (const o of orders) {
      const iso = (o.scheduled_at || "").slice(0, 10);
      if (!iso) continue;
      if (!m[iso]) m[iso] = [];
      m[iso].push(o);
    }
    // ordenar por hora dentro del día
    for (const iso of Object.keys(m)) {
      m[iso].sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
    }
    return m;
  }, [orders]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col" style={{ minHeight: "70vh" }}>
      {/* Header estilo Google Calendar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnchor(new Date())}
            className="px-3 py-1.5 text-xs font-bold border border-gray-300 rounded hover:bg-gray-50"
            data-testid="cal-today-btn"
          >
            Hoy
          </button>
          <button onClick={() => shiftBy(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-prev">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => shiftBy(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-next">
            <ChevronRight size={16} />
          </button>
          <div className="text-lg font-bold text-gray-800 ml-2 capitalize">
            <CalIcon size={16} className="inline mr-2 text-gray-500" />
            {headerLabel}
          </div>
        </div>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden bg-white">
          {MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`px-3 py-1.5 text-xs font-bold ${mode === m.key ? "bg-[#0A3747] text-[#E0E621]" : "text-gray-600 hover:bg-gray-50"}`}
              data-testid={`cal-mode-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {mode === "day"   && <DayView anchor={anchor} orders={ordersByISO[toISODate(anchor)] || []} onOpen={onOpenOrder} todayISO={todayISO} />}
        {mode === "week"  && <WeekView anchor={anchor} ordersByISO={ordersByISO} onOpen={onOpenOrder} todayISO={todayISO} />}
        {mode === "month" && <MonthView anchor={anchor} ordersByISO={ordersByISO} onOpen={onOpenOrder} todayISO={todayISO} />}
      </div>
    </div>
  );
}

/* ─── Vista Día ─────────────────────────────────────────── */
function DayView({ anchor, orders, onOpen, todayISO }) {
  const iso = toISODate(anchor);
  const HOUR_PX = 60; // altura fija por hora
  return (
    <div className="grid" style={{ gridTemplateColumns: "72px 1fr" }}>
      {HOURS.map((h) => {
        // v2.1.4 · Renderizar la OT solo en la HORA DE INICIO. El chip se extiende
        // verticalmente cubriendo tantos slots como dure según el SLA de prioridad.
        const cellOrders = orders.filter((o) => new Date(o.scheduled_at).getHours() === h);
        return (
          <div key={h} className="contents">
            <div className="text-[10px] font-mono text-gray-400 text-right pr-2 -mt-1.5 border-t border-gray-100" style={{ minHeight: HOUR_PX }}>
              {String(h).padStart(2, "0")}:00
            </div>
            <div className="border-t border-l border-gray-100 relative p-1" style={{ minHeight: HOUR_PX }}>
              {cellOrders.map((o, i) => {
                const start = new Date(o.scheduled_at);
                const durH = getSlaHours(o);
                const startMinOffset = start.getMinutes() / 60;
                const top = startMinOffset * HOUR_PX + 4;
                const height = Math.max(24, durH * HOUR_PX - 6);
                return (
                  <EventChip
                    key={o.id}
                    order={o}
                    onOpen={onOpen}
                    full
                    style={{ position: "absolute", left: `${8 + i * 4}px`, right: "8px", top: `${top}px`, height: `${height}px`, zIndex: 5 + i }}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Vista Semana ──────────────────────────────────────── */
function WeekView({ anchor, ordersByISO, onOpen, todayISO }) {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid" style={{ gridTemplateColumns: `60px repeat(7, 1fr)` }}>
      {/* Header sticky */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 h-16"></div>
      {days.map((d) => {
        const iso = toISODate(d);
        const isToday = iso === todayISO;
        return (
          <div key={iso} className="sticky top-0 z-20 bg-white border-b border-l border-gray-100 py-2 text-center">
            <div className="text-[10px] uppercase font-mono tracking-widest text-gray-500">
              {DAYS_ES_SHORT[d.getDay()]}
            </div>
            <div
              className={`text-2xl font-normal mt-0.5 mx-auto ${
                isToday
                  ? "bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold"
                  : "text-gray-700"
              }`}
              style={isToday ? {} : { height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {d.getDate()}
            </div>
          </div>
        );
      })}
      {/* Hours grid */}
      {HOURS.map((h) => (
        <div key={h} className="contents">
          <div className="text-[10px] font-mono text-gray-400 text-right pr-2 -mt-2 border-t border-gray-100" style={{ height: 56 }}>
            {String(h).padStart(2, "0")}:00
          </div>
          {days.map((d) => {
            const iso = toISODate(d);
            // v2.1.4 · Renderizar solo en hora de INICIO; alto según SLA
            const cellOrders = (ordersByISO[iso] || []).filter((o) => new Date(o.scheduled_at).getHours() === h);
            const HOUR_PX = 56;
            return (
              <div key={`${iso}-${h}`} className="border-t border-l border-gray-100 relative p-0.5" style={{ height: HOUR_PX }}>
                {cellOrders.map((o, i) => {
                  const start = new Date(o.scheduled_at);
                  const durH = getSlaHours(o);
                  const startMinOffset = start.getMinutes() / 60;
                  const top = startMinOffset * HOUR_PX + 2;
                  const height = Math.max(20, durH * HOUR_PX - 4);
                  return (
                    <EventChip
                      key={o.id}
                      order={o}
                      onOpen={onOpen}
                      style={{ position: "absolute", left: `${2 + i * 4}px`, right: "2px", top: `${top}px`, height: `${height}px`, zIndex: 5 + i }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ─── Vista Mes ─────────────────────────────────────────── */
function MonthView({ anchor, ordersByISO, onOpen, todayISO }) {
  const start = startOfMonthGrid(anchor);
  const currentMonth = anchor.getMonth();
  const days = Array.from({ length: 42 }, (_, i) => addDays(start, i));

  return (
    <div>
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {DAYS_ES_SHORT.map((d) => (
          <div key={d} className="text-[10px] uppercase font-mono tracking-widest text-gray-500 text-center py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(100px, 1fr)" }}>
        {days.map((d) => {
          const iso = toISODate(d);
          const isToday = iso === todayISO;
          const isOtherMonth = d.getMonth() !== currentMonth;
          const dayOrders = ordersByISO[iso] || [];
          return (
            <div
              key={iso}
              className={`border-t border-l border-gray-100 p-1.5 hover:bg-blue-50/50 transition ${isOtherMonth ? "bg-gray-50 text-gray-400" : "bg-white"}`}
              data-testid={`month-cell-${iso}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-sm font-normal ${
                    isToday
                      ? "bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold"
                      : isOtherMonth ? "text-gray-400" : "text-gray-700"
                  }`}
                >
                  {d.getDate()}
                </span>
                {dayOrders.length > 0 && (
                  <span className="text-[9px] font-mono text-gray-400">{dayOrders.length}</span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayOrders.slice(0, 3).map((o) => (
                  <EventChip key={o.id} order={o} onOpen={onOpen} compact />
                ))}
                {dayOrders.length > 3 && (
                  <div className="text-[9px] text-blue-600 pl-1 font-semibold hover:underline cursor-pointer">
                    + {dayOrders.length - 3} más
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Event chip (bloque de OT en cualquier vista) ──────── */
function EventChip({ order, onOpen, compact, full, style }) {
  const c = STATUS_COLOR[order.status] || STATUS_COLOR.pendiente_agendamiento;
  const t = order.scheduled_at ? new Date(order.scheduled_at) : null;
  // v2.1.4 · Hora fin = inicio + SLA de la prioridad
  const durH = getSlaHours(order);
  const tEnd = t ? new Date(t.getTime() + durH * 3600 * 1000) : null;
  const time = t ? fmtHHMM(t) : "";
  const timeEnd = tEnd ? fmtHHMM(tEnd) : "";
  const timeRange = time && timeEnd ? `${time}–${timeEnd}` : time;
  const positioned = !!style;

  if (compact) {
    return (
      <div
        onClick={(e) => { e.stopPropagation(); onOpen(order.id); }}
        className="text-[10px] font-mono truncate rounded px-1 py-0.5 border-l-2 hover:brightness-95 cursor-pointer"
        style={{ backgroundColor: c.bg, borderLeftColor: c.border, color: c.text }}
        title={`${order.code} · ${order.client_name || ""} · ${timeRange}`}
        data-testid={`event-${order.code}`}
      >
        {time && <span className="font-bold mr-1">{time}</span>}
        {order.code}
      </div>
    );
  }

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onOpen(order.id); }}
      className={`${positioned ? "" : ""} rounded px-1.5 py-0.5 shadow-sm hover:shadow cursor-pointer border-l-4 overflow-hidden`}
      style={{
        backgroundColor: c.bg,
        borderLeftColor: c.border,
        color: c.text,
        ...(style || {}),
      }}
      title={`${order.code} · ${order.client_name || ""} · ${order.priority || ""} · ${timeRange} (SLA ${durH}h)${order.technician_name ? ` · Téc: ${order.technician_name}` : ""}${order.solution_type_name ? ` · ${order.solution_type_name}` : ""}`}
      data-testid={`event-${order.code}`}
    >
      <div className={`${full ? "text-xs" : "text-[10px]"} font-mono truncate leading-tight`}>
        {timeRange && <span className="font-bold mr-1">{timeRange}</span>}
        <span className="font-semibold">{order.code}</span>
        {full && order.client_name && <span className="opacity-75 ml-1">· {order.client_name}</span>}
      </div>
      {/* v2.1.5 · Info extra en chips grandes (Día/Semana) — técnico + solución */}
      {style && style.height && parseInt(style.height) > 32 && (
        <>
          {(order.technician_name || order.assigned_user_name) && (
            <div className="text-[10px] truncate leading-tight opacity-80 mt-0.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
              <span className="truncate">{order.technician_name || order.assigned_user_name}</span>
            </div>
          )}
          {order.solution_type_name && parseInt(style.height) > 50 && (
            <div className="text-[10px] truncate leading-tight opacity-70 italic mt-0.5">
              {order.solution_type_name}
            </div>
          )}
          {order.priority && parseInt(style.height) > 70 && (
            <div className="text-[9px] font-mono uppercase tracking-wider mt-1 inline-block px-1.5 py-0.5 rounded" style={{ background: c.border, color: c.bg }}>
              {order.priority} · SLA {durH}h
            </div>
          )}
        </>
      )}
    </div>
  );
}
